import React from 'react';
import CustomModal from "../../../components/Modal/CustomModal";
import {
    IconAvatarTable,
    IconClockTable,
    IconDeskOfficeTable,
    IconMarkerTable,
    IconOpenEmailCard,
    IconScheduleTable,
    ImgIEntryCouch, PersonOutlinedChip,
    PersonOutlinedTitle
} from "../../../components/buttons/IconButtons";
import CustomCard from "../../../components/CustomCard";
import CustomUserChip from "../../../components/CustomUserChip";
import CustomTooltip from "../../../components/CustomTooltip";
import CustomGenericTittleIconDataChip from "../../../components/CustomGenericTittleIconDataChip";
import {chipStatusClassColor} from "../../../enums/statusColorEnum";
import {status} from "../../../enums/statusEnum";
import {POSITION_MODEL} from "../../../Helpers/PositionModel";
import {EMPTY_VALUES, getTimeFormat} from "../../../Apis/functions";
import {MyApartmentInvitationModalApp} from "../Functionality/MyApartmentInvitationModalApp";
import {getVisitorTypeLabel} from "../../Dashboard/Visitor/Type/Model/Model";

/**
 * @Email : kpozos@ibl.mx
 * @Author: Karen Yunuen Pozos Arias
 * @jobTittle: Full Stack Developer
 *
 * Details of one visit of the logged resident: visitor, date and time of the
 * visit, access zone, type of visitor and the resident that invited them.
 *
 * @param {boolean} show - Flag that opens the modal.
 * @param {Function} onHide - Closes the modal and clears the selected visit.
 * @param {Object} itemSelected - The visit the details are shown for.
 * @returns {JSX.Element}
 * @constructor
 */

const MyApartmentInvitationModal = ({ show, onHide, itemSelected }) => {

    const {
        t, identityValidationId, zoneName, rootZoneName, startDate, endDate, isSameDate
    } = MyApartmentInvitationModalApp({ itemSelected });

    return (
        <CustomModal
            show={show}
            size='lg'
            onHide={() => {
                onHide();
            }}
            confirmButton={false}
            cancelButton={false}
            topLeftHeader={
                <div className={'d-flex align-items-center pb-3'}>
                    <div><IconOpenEmailCard /></div>
                    <p className={'fs-modal-title font-color-general-950 ibl-font-w-600 ml-2 text-ellipsis'}>
                        {t('invitation')}
                    </p>
                    {identityValidationId && (
                        <CustomUserChip
                            className={`mt-0 flex-shrink-0 ${chipStatusClassColor[identityValidationId]}`}
                            name={t(status[identityValidationId]) || EMPTY_VALUES.DEFAULT}
                            showAvatar={false}
                            variant='large'
                            iconPosition={POSITION_MODEL.LEFT}
                        />
                    )}
                </div>
            }
            classNameBody='pt-2'
        >
            <CustomCard classCard={'card-border-8px'} classContent={'p-0'}>
                <ImgIEntryCouch className={'w-100'} />
                <div className={'row'}>
                    <div className={'col-12 my-4'}>
                        <div className={'d-flex justify-content-between align-items-center'}>
                            <div className={'d-flex align-items-center'}>
                                <div><IconAvatarTable /></div>
                                <CustomTooltip
                                    text={itemSelected?.visitorFullName || EMPTY_VALUES.DEFAULT}
                                    classes={'fs-14px font-color-general-950 text-ellipsis ml-3'}
                                    showIfTruncated={true}
                                />
                            </div>
                            {itemSelected?.visitorStatusId && (
                                <div>
                                    <CustomUserChip
                                        className={`mt-0 ${chipStatusClassColor[itemSelected?.visitorStatusId]}`}
                                        name={t(status[itemSelected?.visitorStatusId]) || EMPTY_VALUES.DEFAULT}
                                        showAvatar={false}
                                        variant='large'
                                        iconPosition={POSITION_MODEL.LEFT}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className={'border-bottom-shades-300'} />

                <div className={'row my-4'}>
                    <div className={'col-sm-12 col-md-6 col-lg-6 col-xl-6 col-xxl-6'}>
                        {/******* Date ***********/}
                        <CustomGenericTittleIconDataChip title={t('date')} />
                        <div className={'mt-2'}>
                            <div className={'d-flex align-items-center'}>
                                <IconScheduleTable className='icon-color-general-950' />
                                <p className={'font-color-general-950 fs-14px ml-2'}>
                                    {isSameDate ? startDate : <>
                                        {t('from')}&nbsp;{startDate}
                                        &nbsp;{t('to').toLowerCase()}&nbsp;{endDate}
                                    </>}
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className={'col-sm-12 col-md-6 col-lg-6 col-xl-6 col-xxl-6'}>
                        {/******* Time ***********/}
                        <CustomGenericTittleIconDataChip title={t('time')} />
                        <div className={'mt-2'}>
                            <div className={'d-flex align-items-center'}>
                                <IconClockTable className='icon-color-general-950' />
                                <p className={'font-color-general-950 fs-14px ml-2'}>
                                    {getTimeFormat(itemSelected?.startDate)}
                                    &nbsp;{t('to').toLowerCase()}&nbsp;
                                    {getTimeFormat(itemSelected?.endDate)}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className={'row my-4'}>
                    <div className={'col-sm-12 col-md-6 col-lg-6 col-xl-6 col-xxl-6'}>
                        {/******* Created by ***********/}
                        <CustomGenericTittleIconDataChip title={t('created_by')} />
                        <div className={'mt-2 d-flex align-items-center'}>
                            <div>
                                <PersonOutlinedChip className={'mr-8px mb-1'} />
                            </div>
                            <p className={'font-color-general-950 fs-14px ml-2'}>
                                {itemSelected?.createdBy || EMPTY_VALUES.DEFAULT}
                            </p>
                        </div>
                    </div>
                    <div className={'col-sm-12 col-md-6 col-lg-6 col-xl-6 col-xxl-6'}>
                        {/******* Type of visitor ***********/}
                        <CustomGenericTittleIconDataChip title={t('type_of_visitor')} />
                        <div className={'mt-2 d-flex align-items-center'}>
                            <div>
                                <PersonOutlinedTitle />
                            </div>
                            <span className={'font-color-general-950 fs-14px ml-2'}>
                                {getVisitorTypeLabel(t, itemSelected?.visitorType)}
                            </span>
                        </div>
                    </div>
                </div>

                <div className={'row my-4'}>
                    <div className={'col-12'}>
                        {/******* Access zone ***********/}
                        <CustomGenericTittleIconDataChip title={t('access_zone')} />
                        <div className={'mt-2'}>
                            {rootZoneName &&
                                <div className={'d-flex align-items-center'}>
                                    <div><IconMarkerTable className={'mr-8px mb-1'} /></div>
                                    <p className={'font-color-general-950 fs-14px ml-2'}>
                                        {rootZoneName}
                                    </p>
                                </div>
                            }
                            <div className={'d-flex align-items-center'}>
                                <div>
                                    {rootZoneName
                                        ? <IconDeskOfficeTable className={'mr-8px mb-1'} />
                                        : <IconMarkerTable className={'mr-8px mb-1'} />
                                    }
                                </div>
                                <p className={'font-color-general-950 fs-14px ml-2'}>
                                    {zoneName || EMPTY_VALUES.DEFAULT}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className={'row my-4'}>
                    <div className={'col-12'}>
                        {/******* Instructions ***********/}
                        <CustomGenericTittleIconDataChip title={t('instructions')} />
                        <div className={'mt-2 d-flex align-items-center'}>
                            <p className={'font-color-general-950 fs-14px ml-2'}>
                                {itemSelected?.description || EMPTY_VALUES.DEFAULT}
                            </p>
                        </div>
                    </div>
                </div>
            </CustomCard>
        </CustomModal>
    );
};

export default MyApartmentInvitationModal;
